import { defineSlotRecipe } from '@chakra-ui/react';

/**
 * Плашка: сообщение, которое экран говорит человеку сам, а не показывает как содержимое.
 *
 * Заводится рецептом по той же причине, что плита и чип: десять плашек в продукте были собраны
 * руками и жили по двум несовместимым схемам. Одни красились сплошным тёмным цветом палитры
 * (`red.subtle`, `bg.warning`), который про наши поверхности ничего не знает. Другие - прозрачной
 * заливкой с рамкой того же тона. Правило системы уже выбрано: всё, что может лечь на любую
 * подложку, красится прозрачностью. Плашка лежит где угодно - на странице, в карточке, в окне, -
 * поэтому прозрачная.
 *
 * Тон отвечает на вопрос «что человеку с этим делать», а не «какого это цвета»:
 *
 * - `info` - просто знай. Ничего не сломано, действие не требуется.
 * - `warning` - что-то мешает, и это чинится: закрытый профиль Steam, у тебя уже есть заявка
 *   на эту игру, сервис временно недоступен.
 * - `danger` - заблокировано, и без человека не починится: уведомления запрещены в браузере.
 * - `accent` - предложение. Ничего не мешает, но можно сделать лучше: показать, во что играешь.
 *
 * Пятого тона нет. Если сообщение не ложится ни в один, это повод разобраться в смысле, а не
 * заводить свою заливку: отсюда и взялся фиолетовый ящик с оранжевым значком предупреждения.
 *
 * Размер `sm` - строка-подсказка внутри другого блока: без плитки под значком и без рамки,
 * иначе она спорит с тем, в чём лежит.
 *
 * Размер `bar` - полоса во всю ширину окна под шапкой: состояние всего приложения, а не экрана
 * (нет сети). Скруглений и боковых рамок нет, остаётся нижняя черта, текст в одну строку по
 * центру.
 */
const tone = (palette: string, iconColor: string) => ({
  root: { bgColor: `${palette}.500/10`, borderColor: `${palette}.500/30` },
  icon: { bgColor: `${palette}.500/15`, color: iconColor },
});

export const noticeSlotRecipe = defineSlotRecipe({
  className: 'tp-notice',
  slots: ['root', 'icon', 'content', 'title', 'description', 'actions'],
  base: {
    root: {
      display: 'flex',
      alignItems: 'flex-start',
      flexWrap: 'wrap',
      w: '100%',
      borderWidth: '1px',
      borderRadius: 'l3',
    },
    icon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    // Ширина, ниже которой действия уходят под текст, а не сжимают его в столбик по слову.
    content: { display: 'flex', flexDirection: 'column', gap: 0.5, flex: '1', minW: '160px' },
    title: { textStyle: 'title', color: 'fg.emphasized' },
    description: { textStyle: 'caption', color: 'fg.secondary' },
    actions: { display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 },
  },
  variants: {
    actionLayout: {
      adaptive: {},
    },
    tone: {
      info: tone('blue', 'fg.info'),
      warning: tone('orange', 'fg.warning'),
      danger: tone('red', 'fg.error'),
      accent: tone('purple', 'purple.200'),
    },
    size: {
      md: {
        root: { gap: 3, px: 4, py: 3 },
        icon: { boxSize: 10, borderRadius: 'l2', '& svg': { boxSize: 5 } },
        actions: { w: { base: '100%', sm: 'auto' }, alignSelf: 'center' },
      },
      sm: {
        root: { gap: 2.5, px: 3, py: 2.5, borderWidth: '0' },
        icon: { bgColor: 'transparent', mt: '1px', '& svg': { boxSize: 4 } },
        content: { minW: 0 },
      },
      bar: {
        root: {
          gap: 2,
          px: 4,
          py: 1.5,
          alignItems: 'center',
          justifyContent: 'center',
          flexWrap: 'nowrap',
          borderWidth: '0',
          borderBottomWidth: '1px',
          borderRadius: 'none',
        },
        icon: { bgColor: 'transparent', '& svg': { boxSize: 4 } },
        content: { flex: '0 1 auto', minW: 0 },
        description: { color: 'fg.emphasized', textAlign: 'center' },
      },
    },
  },
  compoundVariants: [
    {
      actionLayout: 'adaptive',
      css: {
        root: { containerType: 'inline-size', alignItems: 'center' },
        content: { minW: '160px' },
        actions: {
          // Override the legacy viewport rule only for this optional layout.
          w: { base: '100%', sm: '100%' },
          '@container (min-width: 480px)': { w: 'auto' },
        },
      },
    },
  ],
  defaultVariants: {
    tone: 'info',
    size: 'md',
  },
});
